import { useEffect, useState, useMemo } from "react";
import {
  Chart as ChartJS,
  BarElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
} from "chart.js";
import { ComposableMap, Geographies, Geography } from "react-simple-maps";
import { scaleLinear } from "d3-scale";
import * as topojson from "topojson-client";

ChartJS.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const topoUrl =
  "https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json";

/* ✅ COUNTRY NAME NORMALIZATION */
const normalizeCountryName = (name) => {
  const map = {
    "United States": "United States of America",
    "USA": "United States of America",
    "UK": "United Kingdom",
    "UAE": "United Arab Emirates",
    "Russia": "Russian Federation",
    "South Korea": "Korea, Republic of",
    "North Korea": "Korea, Democratic People's Republic of",
    "Iran": "Iran, Islamic Republic of",
    "Tanzania": "Tanzania, United Republic of",
    "Bolivia": "Bolivia, Plurinational State of",
    "Venezuela": "Venezuela, Bolivarian Republic of",
    "Ivory Coast": "Côte d'Ivoire",
    "DR Congo": "Congo, Democratic Republic of the",
    "Italy": "Italy",
  };

  return map[name] || name;
};

export default function LiveViewers() {
  const [live, setLive] = useState({ total: 0, countries: [] });
  const [geoData, setGeoData] = useState(null);

  /* Fetch live viewers */
  useEffect(() => {
    const loadLive = () => {
      fetch("https://backend.ysskytravel.com/api/analytics/live")
        .then((r) => r.json())
        .then(setLive)
        .catch(console.error);
    };

    loadLive();
    const interval = setInterval(loadLive, 5000);
    return () => clearInterval(interval);
  }, []);

  /* Fetch map topology */
  useEffect(() => {
    fetch(topoUrl)
      .then((res) => res.json())
      .then((topology) => {
        const geo = topojson.feature(
          topology,
          topology.objects.countries
        );
        setGeoData(geo);
      })
      .catch(console.error);
  }, []);

  /* ✅ BUILD MAP FROM LIVE DATA (THIS WAS THE BUG) */
  const visitsByCountry = useMemo(() => {
    const map = {};
    live.countries.forEach((c) => {
      const normalized = normalizeCountryName(c.country);
      map[normalized] = c.count;
    });
    return map;
  }, [live.countries]);

  const maxVisits = Math.max(...Object.values(visitsByCountry), 1);

  const colorScale = scaleLinear()
    .domain([0, maxVisits])
    .range(["#e0f3ff", "#2f855a"]);

  if (!geoData) return <p>Loading map...</p>;

  return (
    <div className="py-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Live Visitors */}
        <div className="bg-white rounded-xl shadow p-6">
          <h2 className="text-xl font-bold mb-4">Live Visitors</h2>
          <p className="text-4xl font-bold mb-4">{live.total}</p>

          <h3 className="font-semibold mb-2">Active Countries</h3>
          <ul className="space-y-1 max-h-48 overflow-y-auto">
            {live.countries.map((c) => (
              <li
                key={c.country}
                className="flex justify-between px-3 py-1 bg-gray-100 rounded-full text-gray-700"
              >
                <span>{c.country}</span>
                <span>{c.count}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Live Map */}
        <div className="bg-white rounded-xl shadow p-6 relative">
          <h2 className="text-xl font-bold mb-4">Live Map</h2>

          <ComposableMap projectionConfig={{ scale: 150 }}>
            <Geographies geography={geoData}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const countryName = geo.properties.name;
                  const visits = visitsByCountry[countryName] || 0;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      fill={colorScale(visits)}
                      stroke="#fff"
                      style={{
                        default: { outline: "none" },
                        hover: {
                          fill: "#00aaff",
                          outline: "none",
                          cursor: "pointer",
                        },
                        pressed: { outline: "none" },
                      }}
                      onMouseEnter={(e) => {
                        const tooltip = document.getElementById("tooltip");
                        if (tooltip) {
                          tooltip.innerHTML = `${countryName}: ${visits}`;
                          tooltip.style.display = "block";
                        }
                      }}
                      onMouseMove={(e) => {
                        const tooltip = document.getElementById("tooltip");
                        if (tooltip) {
                          tooltip.style.top = e.clientY + 10 + "px";
                          tooltip.style.left = e.clientX + 10 + "px";
                        }
                      }}
                      onMouseLeave={() => {
                        const tooltip = document.getElementById("tooltip");
                        if (tooltip) tooltip.style.display = "none";
                      }}
                    />
                  );
                })
              }
            </Geographies>
          </ComposableMap>

          <div
            id="tooltip"
            className="absolute bg-black text-white text-sm px-2 py-1 rounded shadow-lg pointer-events-none"
            style={{ display: "none" }}
          />
        </div>
      </div>
    </div>
  );
}
